import "core-js/modules/es.symbol.description.js";
const _excluded = ["user", "onLogin", "onLogout", "navLinks", "logoUrl", "buttonVariant", "themeGroup", "userLinks"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, AppBar as MUIAppBar, Toolbar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Button from "./Button.js";
import UserMenu from "./UserMenu.js";
import PermissionFilter from "./PermissionFilter.js"; //We are making the bold and, hopefully, correct assumption that your application will always use 'Can Sign In' as the permission string
import { ACLS } from "../constants.js";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const theTheme = {
  appBar: {
    logo: {
      height: '100%',
      width: 'auto',
      maxHeight: '44px',
      top: '3px',
      position: 'relative'
    }
  }
};

/**
 * App Bar Component for the application
 * @param {object} props
 * @param {object} props.user - The user object from the authState
 * @param {function} props.onLogout - A logout function to call when the user clicks the logout button
 * @param {function} props.onLogin - The login function to call when the user clicks the login button
 * @param {array} props.navLinks - An array of objects to render as nav links
 * @param {string} props.navLinks[].title - The label for the nav link
 * @param {string} props.navLinks[].href - The path for the nav link
 * @param {string} props.logoUrl - The url for the logo
 * @param {string} props.buttonVariant - The MUI variant name for the buttons creating by navLinks
 */
const AppBar = _ref => {
  let {
      user,
      onLogin,
      onLogout,
      navLinks,
      logoUrl,
      buttonVariant = 'appbar',
      themeGroup,
      userLinks
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const theme = useTheme();
  const appBar = (theme === null || theme === void 0 ? void 0 : theme.appBar) || theTheme.appBar;

  // If a theme group was passed in, use that instead of the default
  const theming = themeGroup || appBar;
  const logoStyle = (theming === null || theming === void 0 ? void 0 : theming.logo) || theTheme.appBar.logo;

  // Helper render method to simplify the final render returned
  const renderMenu = () => {
    if (user) {
      return /*#__PURE__*/_jsxs(Stack, {
        spacing: 10,
        direction: "row",
        children: [/*#__PURE__*/_jsx(Stack, {
          spacing: 2,
          direction: "row",
          children: navLinks.map(renderButton)
        }), renderUserArea()]
      });
    }
    return renderUserArea();
  };

  // On the off chance a button is defined that does not need special permission render it with the PermissionFilter.
  const renderButton = (item, index) => {
    const theButton = /*#__PURE__*/_jsx(Button, {
      end: true,
      variant: buttonVariant,
      disableElevation: true,
      href: item.href,
      label: item.title
    }, index);
    if (item.permission) {
      return /*#__PURE__*/_jsx(PermissionFilter, {
        permission: item.permission,
        children: theButton
      }, index);
    }
    return theButton;
  };

  // Render the user area if the user is allowed to sign in.
  const renderUserArea = () => {
    return /*#__PURE__*/_jsx(PermissionFilter, {
      permission: ACLS.SIGN_IN,
      debug: "UserMenu",
      children: /*#__PURE__*/_jsx(UserMenu, {
        user: user,
        onLogin: onLogin,
        onLogout: onLogout,
        sx: {
          position: 'absolute',
          left: '0px'
        },
        links: userLinks
      })
    });
  };
  return /*#__PURE__*/_jsx(Box, {
    sx: {
      flexGrow: 1,
      height: '64px'
    },
    children: /*#__PURE__*/_jsx(MUIAppBar, _objectSpread(_objectSpread({
      sx: {
        zIndex: theme => theme.zIndex.drawer + 1
      },
      position: "relative"
    }, props), {}, {
      children: /*#__PURE__*/_jsxs(Toolbar, {
        children: [/*#__PURE__*/_jsx(Box, {
          sx: {
            flexGrow: 1
          },
          children: logoUrl ? /*#__PURE__*/_jsx("img", {
            alt: "Logo",
            src: logoUrl,
            style: logoStyle,
            className: "appbar-logo"
          }) : /*#__PURE__*/_jsx("p", {
            children: "Logo"
          })
        }), renderMenu()]
      })
    }))
  });
};
AppBar.propTypes = {
  user: PropTypes.shape({}),
  onLogin: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  navLinks: PropTypes.array,
  logoUrl: PropTypes.string,
  userLinks: PropTypes.array
};
AppBar.defaultProps = {
  navLinks: [{
    title: 'Home',
    href: '/'
  }, {
    title: 'Communities',
    href: '/communities'
  }, {
    title: 'Plans',
    href: '/pam/plans'
  }, {
    title: 'Explorer',
    href: '/explorer/hvra'
  }],
  user: null,
  logoUrl: 'https://wilcity.com/wp-content/uploads/2018/12/sample-logo-design-png-3-2.png' //This is a lorum ipusum logo
};

export default AppBar;